/* ===========================================================================
   assets/tg-platform-chooser.js
   ---------------------------------------------------------------------------
   The chooser above the Platforms grid. Answers two questions a collection
   page cannot answer on its own:

     "which of these fits MY car?"    -> the car dropdowns
     "what is the difference?"        -> a function line on every card, and a
                                         PRO-vs-LEVEL line that never hides

   IT DOES NOT FILTER THE GRID. Products that do not fit are dimmed and sorted
   last, never removed. A visitor who cannot see a product cannot learn why it
   was wrong for them, and a grid that empties itself looks broken.

   FITMENT LIVES IN ONE PLACE
     window.TGFit, published by tg-vehicle-selector.js, which this file loads
     alongside. If that file is missing the chooser still renders — it just
     stops offering verdicts, rather than guessing.

   The chosen car is written to the same localStorage key the product-page
   selector uses, so picking a car here carries through to the product page.

   Asset. Loaded once, guarded. No dependencies beyond TGFit.
   =========================================================================== */
(function () {
  'use strict';
  if (window.__tgPlatformChooserReady) return;
  window.__tgPlatformChooserReady = true;
  window.__tgChooserVersion = '2026-09-06';

  var LS_KEY = 'tg_vehicle_v1';   // shared with tg-vehicle-selector.js

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function readJSON(id) {
    var n = id && document.getElementById(id);
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { return null; }
  }
  function save(o) { try { localStorage.setItem(LS_KEY, JSON.stringify(o)); } catch (e) {} }
  function load() { try { return JSON.parse(localStorage.getItem(LS_KEY) || 'null'); } catch (e) { return null; } }

  function orderMakes(list, pinned) {
    var top  = (pinned || []).map(function (s) { return String(s).trim(); }).filter(Boolean);
    var head = top.filter(function (m) { return list.indexOf(m) > -1; });
    var tail = list.filter(function (m) { return head.indexOf(m) === -1; })
                   .sort(function (a, b) { return a.localeCompare(b); });
    return head.concat(tail);
  }

  /* ------------------------------------------------------------- chooser -- */
  function Chooser(root, data, cfg) {
    this.root = root;
    this.data = data || {};
    this.cfg  = cfg  || {};
    this.items = (this.cfg.items || []).slice();
    this.need = '';                      // '' = no filter, show everything
    this.make = this.model = this.gen = '';
    this.build();
    this.restore();
    this.render();
  }

  Chooser.prototype.build = function () {
    var self = this;

    var step1 = el('div', 'tg-pc__step');
    var l1 = el('p', 'tg-pc__legend', this.cfg.carLabel || 'Your car');
    step1.appendChild(l1);
    var row1 = el('div', 'tg-pc__row');

    this.fMake  = this.field(row1, 'Make');
    this.fModel = this.field(row1, 'Model');
    this.fGen   = this.field(row1, 'Generation');
    this.fModel.wrap.hidden = true;
    this.fGen.wrap.hidden   = true;
    step1.appendChild(row1);

    var step2 = el('div', 'tg-pc__step');
    step2.appendChild(el('p', 'tg-pc__legend', this.cfg.needLabel || 'What you want it to do'));
    var row2 = el('div', 'tg-pc__row');
    this.chips = (this.cfg.needs || []).map(function (n) {
      var b = el('button', 'tg-pc__chip', n.label);
      b.type = 'button';
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', function () {
        self.need = (self.need === n.key) ? '' : n.key;   // second click clears
        self.render();
      });
      row2.appendChild(b);
      return { key: n.key, node: b };
    });
    step2.appendChild(row2);

    this.cards = el('ul', 'tg-pc__cards');

    /* A heading for the list, not for the section.
       ------------------------------------------------------------------
       The sentence below already existed — "4 of 7 platforms fit a Tesla
       Model Y" — but it was written only into the aria-live region, which
       is visually hidden. Screen-reader users were told what the grid had
       become; everyone else was left to infer it from the cards reordering.

       That is the wrong way round. The section heading is fixed copy and
       cannot say it: it is written before anyone has chosen a car. So the
       answer belongs here, directly above the cards, where it reads as a
       label for the list rather than a title for the page.

       It stays empty until a car is chosen, because until then there is
       nothing true to say. */
    this.status = el('p', 'tg-pc__status');
    this.status.hidden = true;

    this.live  = el('p', 'tg-pc__sr');
    this.live.setAttribute('aria-live', 'polite');

    this.root.appendChild(step1);
    if (this.chips.length) this.root.appendChild(step2);
    this.root.appendChild(this.status);
    this.root.appendChild(this.cards);
    this.root.appendChild(this.live);

    if (this.cfg.compare) {
      var vs = el('div', 'tg-pc__vs');
      vs.innerHTML = this.cfg.compare;
      this.root.appendChild(vs);
    }

    var makes = orderMakes(Object.keys(this.data), this.cfg.pinnedMakes);
    this.fill(this.fMake.sel, this.cfg.makePlaceholder || 'Choose make', makes,
              (this.cfg.pinnedMakes || []).length);

    this.fMake.sel.addEventListener('change', function () { self.onMake(); });
    this.fModel.sel.addEventListener('change', function () { self.onModel(); });
    this.fGen.sel.addEventListener('change', function () {
      self.gen = self.fGen.sel.value; self.persist(); self.render();
    });
  };

  Chooser.prototype.field = function (row, label) {
    var wrap = el('div', 'tg-pc__field');
    var sel  = el('select', 'tg-pc__select');
    sel.setAttribute('aria-label', label);
    wrap.appendChild(sel);
    row.appendChild(wrap);
    return { wrap: wrap, sel: sel };
  };

  Chooser.prototype.fill = function (sel, placeholder, list, dividerAfter) {
    sel.innerHTML = '';
    var o = el('option', null, placeholder);
    o.value = '';
    sel.appendChild(o);
    list.forEach(function (v, i) {
      var opt = el('option', null, v);
      opt.value = v;
      sel.appendChild(opt);
      if (dividerAfter && i === dividerAfter - 1 && i < list.length - 1) {
        var d = el('option', null, '──────────');
        d.disabled = true;
        sel.appendChild(d);
      }
    });
  };

  Chooser.prototype.onMake = function () {
    this.make = this.fMake.sel.value;
    this.model = this.gen = '';
    this.fGen.wrap.hidden = true;
    var models = this.make ? Object.keys(this.data[this.make] || {}).sort() : [];
    this.fModel.wrap.hidden = !models.length;
    if (models.length) this.fill(this.fModel.sel, this.cfg.modelPlaceholder || 'Choose model', models);
    this.persist();
    this.render();
  };

  Chooser.prototype.onModel = function () {
    this.model = this.fModel.sel.value;
    this.gen = '';
    var gens = this.gensFor(this.row());
    this.fGen.wrap.hidden = gens.length < 2;
    if (gens.length > 1) this.fill(this.fGen.sel, this.cfg.genPlaceholder || 'Choose version', gens);
    this.persist();
    this.render();
  };

  Chooser.prototype.row = function () {
    if (!this.make || !this.model) return null;
    var m = this.data[this.make];
    return (m && m[this.model]) || null;
  };

  /* Generations are only worth asking about when they differ — either because
     PRO cuts a different build for each, or because one carries its own note. */
  Chooser.prototype.gensFor = function (row) {
    if (!row || !row.pro) return [];
    var keys = Object.keys(row.pro);
    return keys.length > 1 ? keys : [];
  };

  Chooser.prototype.persist = function () {
    save({ make: this.make, model: this.model, gen: this.gen });
  };

  Chooser.prototype.restore = function () {
    var want = load() || this.parseDefault(this.cfg.defaultCar);
    if (!want || !want.make || !this.data[want.make]) return;
    this.fMake.sel.value = want.make;
    this.onMake();
    if (!want.model || !(this.data[want.make] || {})[want.model]) return;
    this.fModel.sel.value = want.model;
    this.onModel();
    if (want.gen && this.gensFor(this.row()).indexOf(want.gen) > -1) {
      this.fGen.sel.value = want.gen;
      this.gen = want.gen;
    }
  };

  Chooser.prototype.parseDefault = function (str) {
    if (typeof str !== 'string' || !str) return null;
    var p = str.split('|');
    return { make: (p[0] || '').trim(), model: (p[1] || '').trim(), gen: (p[2] || '').trim() };
  };

  /* --------------------------------------------------------------- render - */
  Chooser.prototype.verdict = function (item) {
    var row = this.row();
    if (!row || !window.TGFit || !item.role) return null;

    /* On a car with several generations and none chosen yet, the visitor has
       asked a model-level question — "does PRO fit a Forester?" — and the
       answer is yes, even though WHICH build depends on the year. verdictFor
       needs a generation and would say "made to order" until one is picked,
       which reads as a no. fitsAny answers the question actually asked. */
    if (!this.gen && this.gensFor(row).length > 1 &&
        window.TGFit.fitsAny(item.role, row)) return 'yes';

    return window.TGFit.verdictFor(item.role, row, this.gen);
  };

  Chooser.prototype.render = function () {
    var self = this;
    this.fMake.wrap.classList.toggle('is-set', !!this.make);
    this.fModel.wrap.classList.toggle('is-set', !!this.model);
    this.fGen.wrap.classList.toggle('is-set', !!this.gen);
    this.chips.forEach(function (c) {
      c.node.setAttribute('aria-pressed', String(c.key === self.need));
    });

    var row = this.row();
    var carName = row ? [this.make, this.model, this.gen].filter(Boolean).join(' ') : '';

    /* Rank, do not remove. Fits first, made to order next, then the rest;
       anything the need filter excludes sinks below all of those. */
    var rank = { yes: 0, custom: 1, unknown: 2, no: 3 };

    /* A few products are extras for one or two cars — the Model Y SOLO, the CAB
       over the front seats. Shown to everyone they are noise; shown to a Model Y
       owner they are the reason to stay. Those carry hide_unless_fits and drop
       out entirely rather than sitting there greyed.

       This is deliberately opt-in and deliberately rare. The main platforms are
       never hidden: a visitor who cannot see PRO cannot learn why it was wrong
       for them. Hiding is for extras, not for the range. */
    var visible = this.items.filter(function (item) {
      if (!item.hide_unless_fits) return true;
      return self.verdict(item) === 'yes';
    });

    var list = visible.map(function (item) {
      var v = self.verdict(item);
      var off = !!(self.need && (item.needs || []).indexOf(self.need) === -1);
      return { item: item, verdict: v, off: off, misfit: v === 'no',
               sort: (off ? 10 : 0) + (v ? rank[v] : 1.5) };
    }).sort(function (a, b) {
      if (a.sort !== b.sort) return a.sort - b.sort;
      return self.items.indexOf(a.item) - self.items.indexOf(b.item);
    });

    /* Two reasons to dim, and together they can dim everything: ask for
       "travelling solo" in a compact hatchback and SOLO is the only product
       that answers, on a car it does not fit. Rather than present a page of
       grey, the fitment dimming stands down and the answer is given in words:
       here is the thing you asked for, and here is why it will not work. */
    var anyLive = list.some(function (e) { return !e.off && !e.misfit; });
    list.forEach(function (e) { e.dim = anyLive ? (e.off || e.misfit) : e.off; });

    this.cards.innerHTML = '';
    list.forEach(function (e) { self.cards.appendChild(self.card(e, row)); });

    if (this.emptyNote) { this.root.removeChild(this.emptyNote); this.emptyNote = null; }
    if (!anyLive && this.need && carName && this.cfg.emptyNote) {
      this.emptyNote = el('p', 'tg-pc__empty',
        String(this.cfg.emptyNote).split('{car}').join(carName));
      this.root.insertBefore(this.emptyNote, this.cards);
    }

    var fits = list.filter(function (e) { return e.verdict === 'yes' && !e.off; }).length;

    /* One sentence, two audiences. The visible line labels the grid; the
       aria-live line announces the change. Same numbers, phrased for each —
       a live region that fires on every keystroke of a dropdown is noise, so
       it keeps the plain form while the visible line can carry emphasis.

       {n}, {total} and {car} are filled here rather than in the template so
       the whole string stays editable from the theme editor. */
    if (carName) {
      var tpl = this.cfg.fitLine || '{n} of {total} fit your {car}';
      this.status.innerHTML = '';
      String(tpl).split('{car}').join(carName)
                 .split('{n}').join(String(fits))
                 .split('{total}').join(String(list.length))
                 .split('\n').forEach(function (chunk, i) {
        if (i) self.status.appendChild(document.createTextNode(' '));
        self.status.appendChild(document.createTextNode(chunk));
      });
      this.status.hidden = false;
      this.live.textContent = fits + ' of ' + list.length + ' platforms fit a ' + carName + '.';
    } else {
      this.status.textContent = '';
      this.status.hidden = true;
      this.live.textContent = '';
    }
  };

  /* ------------------------------------------------------------- the badge */
  /* Static markup, authored here — never anything from a setting or a
     product. Safe to hand to innerHTML; do not extend this with user text. */
  /* "FITS" IS A SHIELD WITH A TICK IN IT

     A bare check mark is the shape of a checkbox, however large it is drawn —
     that was the note twice over. Putting it inside a shield fixes it: the
     outline is not a control anyone has ever clicked, so the mark stops
     reading as "selected" and starts reading as "verified". The tick is still
     in there doing the work of saying yes.

     Two elements at badge size need care. The shield is a thinner line than
     the tick inside it, so the tick stays the thing you see first, and the
     yes badge is larger than the other three to give both room.

     (Earlier attempts, for the record: a plain tick read as a checkbox, and a
     car silhouette at 17px turned out to be a turtle.) */
  var ICONS = {
    yes:     '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2.7c2.5 1.6 5 2.4 7.6 2.5v6.3c0 4.6-3.1 8.1-7.6 9.5-4.5-1.4-7.6-4.9-7.6-9.5V5.2C7 5.1 9.5 4.3 12 2.7z" stroke-width="1.7"/><path d="M8.5 12.1l2.6 2.7 4.6-4.9" stroke-width="2.6"/></svg>',
    no:      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/></svg>',
    custom:  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.4"/><path d="M12 7.2V12l3.1 2"/></svg>',
    unknown: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="8.4"/><path d="M9.7 9.6a2.4 2.4 0 1 1 3 2.3c-.6.2-.9.7-.9 1.3v.4"/><path d="M11.9 16.6h.02"/></svg>'
  };

  /* WHY YES AND NO GET A BARE ICON AND THE OTHER TWO DO NOT

     A tick and a cross are understood without a caption. "Made to order" and
     "ask us" are not — there is no glyph that says "we will build this for
     you", which is why the original verdict line deliberately gave that state
     words and no mark. Shrinking it to an icon would have thrown the meaning
     away to save space on the two states that needed it most.

     So the badge is a pill: icon alone where the icon is enough, icon plus a
     short word where it is not. The full sentence stays on every one of them,
     as a title for the mouse and as screen-reader text inside the link, so
     nothing is lost for anyone. */
  function verdictBadge(v, full, words) {
    var kind = (v === 'yes' || v === 'no' || v === 'custom') ? v : 'unknown';
    var b = el('span', 'tg-pc__badge is-' + kind);
    b.setAttribute('title', full);
    b.innerHTML = ICONS[kind];

    if (kind === 'custom' || kind === 'unknown') {
      var shortWord = kind === 'custom'
        ? (words.customShort  || 'Made to order')
        : (words.unknownShort || 'Ask us');
      b.appendChild(el('span', 'tg-pc__badge-txt', shortWord));
      b.classList.add('has-text');
    }

    b.appendChild(el('span', 'tg-pc__sr', full));
    return b;
  }

  Chooser.prototype.card = function (entry, row) {
    var item = entry.item;
    var li = el('li');
    var a  = el('a', 'tg-pc__card');
    a.href = item.url || '#';
    if (entry.dim) a.classList.add('is-out');

    var v = entry.verdict;
    var words = this.cfg.verdicts || {};
    var full = '';
    if (v === 'yes')         full = words.yes     || 'Fits your car';
    else if (v === 'custom') full = words.custom  || 'Made to order for your car';
    else if (v === 'no')     full = words.no      || 'Does not fit this car';
    else if (v)              full = words.unknown || 'Ask us about this car';

    if (item.image) {
      /* The image gets a positioned wrapper purely so the badge has a corner
         to sit in. Without it the badge would anchor to the whole card. */
      var media = el('div', 'tg-pc__media');
      var img = el('img', 'tg-pc__img');
      img.src = item.image;
      img.alt = item.alt || item.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
      if (v) media.appendChild(verdictBadge(v, full, words));
      a.appendChild(media);
    }

    var b = el('div', 'tg-pc__body');
    b.appendChild(el('h3', 'tg-pc__name', item.title || ''));
    if (item.role_line) b.appendChild(el('p', 'tg-pc__role', item.role_line));

    /* No photo means no corner to pin the badge to, so that card keeps the
       original line of text. A product without an image would otherwise lose
       its verdict entirely — the one case where saving space costs the answer. */
    if (v && !item.image) {
      var cls = (v === 'yes') ? 'is-yes' : (v === 'no') ? 'is-no' : 'is-warn';
      var p = el('p', 'tg-pc__verdict ' + cls, full);
      b.appendChild(p);
    }

    if (item.fn) b.appendChild(el('p', 'tg-pc__fn', item.fn));

    /* Notes tied to a verdict. A product that is narrow on purpose should say
       so in its own words — "does not fit this car" is true but reads like a
       dead end when the honest answer is "not yet". */
    if (v === 'no'     && item.no_note)     b.appendChild(el('p', 'tg-pc__note', item.no_note));
    if (v === 'custom' && item.custom_note) b.appendChild(el('p', 'tg-pc__note', item.custom_note));

    /* BOX is the LEVEL frame with a kitchen on it, so a bed can be added to it
       later — but only in a boot long enough to sleep in. Promising the upgrade
       on a car that can never take it is the sort of thing a buyer discovers
       after paying, so the grade decides whether the line appears at all. */
    if (v === 'yes' && item.upgrade_note && row &&
        (item.upgrade_grades || [1, 2]).indexOf(row.g) > -1) {
      b.appendChild(el('p', 'tg-pc__note', item.upgrade_note));
    }

    /* A per-car note from the data file — the deep floor, the folded third row.
       Only ever shown against a product it actually applies to. */
    if (row && window.TGFit && item.role === 'pro') {
      var note = window.TGFit.attr(row, this.gen, 'n');
      if (note) b.appendChild(el('p', 'tg-pc__note', note));
    }

    if (item.price) b.appendChild(el('p', 'tg-pc__price', item.price));
    a.appendChild(b);
    li.appendChild(a);
    return li;
  };

  /* ------------------------------------------------------------------ boot */
  function boot() {
    var data = readJSON('tg-vehicle-data');
    if (!data) return;
    $$('[data-tg-platform-chooser]').forEach(function (root) {
      if (root.__tgDone) return;
      var cfg = readJSON(root.getAttribute('data-config-id'));
      if (!cfg) return;
      root.__tgDone = true;
      root.__tgChooser = new Chooser(root, data, cfg);
    });
  }

  document.addEventListener('shopify:section:load', boot);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();